import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Slider,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';

const DEFAULT_CROP_CONFIG = {
  enable: false,
  min_crop_area: 15625,
  min_crop_width: 125,
  min_crop_height: 125,
  max_crop_area: null,
  max_crop_width: null,
  max_crop_height: null,
};
const MIN_FACE_CROP_PIXELS = 32;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normalizePositiveInt = (value, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.round(parsed);
};

const buildStateFromConfig = (config = {}, frameLimit = 1080, minLimit = MIN_FACE_CROP_PIXELS) => {
  const merged = { ...DEFAULT_CROP_CONFIG, ...(config || {}) };
  const minFromConfig = Math.max(
    normalizePositiveInt(merged.min_crop_width, 125),
    normalizePositiveInt(merged.min_crop_height, 125),
  );
  const maxFromConfig =
    merged.max_crop_width || merged.max_crop_height
      ? Math.max(
          normalizePositiveInt(merged.max_crop_width, minFromConfig),
          normalizePositiveInt(merged.max_crop_height, minFromConfig),
        )
      : frameLimit;

  const minPixels = clamp(minFromConfig, minLimit, frameLimit);
  const maxPixels = clamp(Math.max(maxFromConfig, minPixels), minPixels, frameLimit);

  return {
    enable: Boolean(merged.enable),
    minPixels,
    maxPixels,
  };
};

const buildCropPayload = ({ enable, minPixels, maxPixels }) => ({
  enable: Boolean(enable),
  min_crop_area: minPixels * minPixels,
  min_crop_width: minPixels,
  min_crop_height: minPixels,
  max_crop_area: maxPixels * maxPixels,
  max_crop_width: maxPixels,
  max_crop_height: maxPixels,
});

const FaceCropModal = ({
  open,
  onClose,
  cameraType,
  cameraLabel,
  imageUrl,
  initialConfig,
  onFetchFrame,
  onSave,
  loading = false,
  saving = false,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [cropState, setCropState] = useState(buildStateFromConfig(DEFAULT_CROP_CONFIG));
  const [cropPositions, setCropPositions] = useState({ min: null, max: null });
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const activeDragRef = useRef(null);

  const frameLimit = useMemo(() => {
    const minDimension = Math.min(imageSize.width || 0, imageSize.height || 0);
    return minDimension > 0 ? minDimension : 1080;
  }, [imageSize.height, imageSize.width]);
  const sliderMin = Math.min(MIN_FACE_CROP_PIXELS, frameLimit);

  useEffect(() => {
    if (!open) return;
    setError('');
    const nextState = buildStateFromConfig(initialConfig, frameLimit, sliderMin);
    setCropState((prev) => {
      if (
        prev.enable === nextState.enable &&
        prev.minPixels === nextState.minPixels &&
        prev.maxPixels === nextState.maxPixels
      ) {
        return prev;
      }
      return nextState;
    });
  }, [frameLimit, initialConfig, open, sliderMin]);

  const getCenteredPosition = useCallback((size) => ({
    x: Math.round((imageSize.width - size) / 2),
    y: Math.round((imageSize.height - size) / 2),
  }), [imageSize.height, imageSize.width]);

  const clampBoxPosition = useCallback((position, size) => {
    if (!imageSize.width || !imageSize.height) return position;
    const boxSize = clamp(size, 1, Math.min(imageSize.width, imageSize.height));
    const fallback = getCenteredPosition(boxSize);
    const rawPosition = position || fallback;
    return {
      x: clamp(Math.round(rawPosition.x), 0, imageSize.width - boxSize),
      y: clamp(Math.round(rawPosition.y), 0, imageSize.height - boxSize),
    };
  }, [getCenteredPosition, imageSize.height, imageSize.width]);

  const getBoxPosition = useCallback((kind) => {
    const size = kind === 'min' ? cropState.minPixels : cropState.maxPixels;
    return clampBoxPosition(cropPositions[kind], size);
  }, [clampBoxPosition, cropPositions, cropState.maxPixels, cropState.minPixels]);

  useEffect(() => {
    if (!imageSize.width || !imageSize.height) return;
    setCropPositions((prev) => {
      const next = {
        min: clampBoxPosition(prev.min || getCenteredPosition(cropState.minPixels), cropState.minPixels),
        max: clampBoxPosition(prev.max || getCenteredPosition(cropState.maxPixels), cropState.maxPixels),
      };
      if (
        prev.min?.x === next.min?.x &&
        prev.min?.y === next.min?.y &&
        prev.max?.x === next.max?.x &&
        prev.max?.y === next.max?.y
      ) {
        return prev;
      }
      return next;
    });
  }, [
    clampBoxPosition,
    cropState.maxPixels,
    cropState.minPixels,
    getCenteredPosition,
    imageSize.height,
    imageSize.width,
  ]);

  const drawBox = useCallback((ctx, kind, size, color, label, fillColor) => {
    if (!ctx || !size || !imageSize.width || !imageSize.height) return;

    const boxSize = clamp(size, 1, Math.min(imageSize.width, imageSize.height));
    const position = getBoxPosition(kind);
    const x = position.x;
    const y = position.y;

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(2, Math.round(imageSize.width / 500));
    ctx.fillStyle = fillColor;
    ctx.fillRect(x, y, boxSize, boxSize);
    ctx.strokeRect(x, y, boxSize, boxSize);
    ctx.fillStyle = color;
    ctx.font = `${Math.max(14, Math.round(imageSize.width / 70))}px Arial`;
    ctx.fillText(label, x + 8, y + 22);
    ctx.restore();
  }, [getBoxPosition, imageSize.height, imageSize.width]);

  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl || !imageLoaded) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      drawBox(ctx, 'max', cropState.maxPixels, '#ef4444', `Max ${cropState.maxPixels}px`, 'rgba(239, 68, 68, 0.08)');
      drawBox(ctx, 'min', cropState.minPixels, '#f97316', `Min ${cropState.minPixels}px`, 'rgba(249, 115, 22, 0.16)');
    };

    img.src = imageUrl;
  }, [cropState.maxPixels, cropState.minPixels, drawBox, imageLoaded, imageUrl]);

  useEffect(() => {
    if (!open || loading || !imageUrl || !canvasRef.current) {
      setImageLoaded(false);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const container = containerRef.current;
      const maxWidth = container ? container.clientWidth - 32 : 900;
      const maxHeight = 620;
      let scale = 1;
      if (img.width > maxWidth) scale = maxWidth / img.width;
      if (img.height * scale > maxHeight) scale = maxHeight / img.height;

      const nextDisplaySize = {
        width: Math.floor(img.width * scale),
        height: Math.floor(img.height * scale),
      };

      canvas.width = img.width;
      canvas.height = img.height;
      setImageSize({ width: img.width, height: img.height });
      setDisplaySize(nextDisplaySize);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, img.width, img.height);
      setImageLoaded(true);
      setError('');
    };

    img.onerror = () => {
      setImageLoaded(false);
      setError('Failed to load camera frame image');
    };

    img.src = imageUrl;
  }, [imageUrl, loading, open]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  const handlePixelChange = (field) => (_, value) => {
    const nextValue = Array.isArray(value) ? value[0] : value;
    setCropState((prev) => {
      if (field === 'minPixels') {
        const minPixels = clamp(Math.round(nextValue), sliderMin, frameLimit);
        return {
          ...prev,
          minPixels,
          maxPixels: Math.max(prev.maxPixels, minPixels),
        };
      }
      const maxPixels = clamp(Math.round(nextValue), prev.minPixels, frameLimit);
      return { ...prev, maxPixels };
    });
  };

  const handlePixelInputChange = (field) => (event) => {
    const raw = event.target.value;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    setCropState((prev) => {
      if (field === 'minPixels') {
        const minPixels = clamp(Math.round(parsed), sliderMin, frameLimit);
        return {
          ...prev,
          minPixels,
          maxPixels: Math.max(prev.maxPixels, minPixels),
        };
      }
      return {
        ...prev,
        maxPixels: clamp(Math.round(parsed), prev.minPixels, frameLimit),
      };
    });
  };

  const handleSave = () => {
    if (cropState.minPixels > cropState.maxPixels) {
      setError('Min crop pixels cannot be greater than max crop pixels.');
      return;
    }
    setSaveConfirmOpen(true);
  };

  const handleConfirmSave = () => {
    setSaveConfirmOpen(false);
    onSave(buildCropPayload(cropState));
  };

  const getCanvasPoint = useCallback((event) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const source = event.touches?.[0] || event.changedTouches?.[0] || event;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: (source.clientX - rect.left) * (canvas.width / rect.width),
      y: (source.clientY - rect.top) * (canvas.height / rect.height),
    };
  }, []);

  const getHitBoxKind = useCallback((point) => {
    if (!point) return null;
    const candidates = [
      { kind: 'min', size: cropState.minPixels },
      { kind: 'max', size: cropState.maxPixels },
    ];
    for (const item of candidates) {
      const position = getBoxPosition(item.kind);
      const size = clamp(item.size, 1, Math.min(imageSize.width, imageSize.height));
      if (
        point.x >= position.x &&
        point.x <= position.x + size &&
        point.y >= position.y &&
        point.y <= position.y + size
      ) {
        return item.kind;
      }
    }
    return null;
  }, [cropState.maxPixels, cropState.minPixels, getBoxPosition, imageSize.height, imageSize.width]);

  const handleCanvasPointerDown = (event) => {
    try {
      if (!imageLoaded || loading || saving) return;
      const point = getCanvasPoint(event);
      const kind = getHitBoxKind(point);
      if (!kind || !point) return;
      event.preventDefault();
      const position = getBoxPosition(kind);
      activeDragRef.current = {
        kind,
        offsetX: point.x - position.x,
        offsetY: point.y - position.y,
      };
    } catch (err) {
      activeDragRef.current = null;
      const message = err?.message || 'Unable to start crop box drag';
      setError(message);
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleCanvasPointerMove = (event) => {
    try {
      const drag = activeDragRef.current;
      if (!drag) return;
      const point = getCanvasPoint(event);
      if (!point) return;
      event.preventDefault();
      const size = drag.kind === 'min' ? cropState.minPixels : cropState.maxPixels;
      const nextPosition = clampBoxPosition(
        {
          x: point.x - drag.offsetX,
          y: point.y - drag.offsetY,
        },
        size,
      );
      setCropPositions((prev) => {
        const current = prev[drag.kind];
        if (current?.x === nextPosition.x && current?.y === nextPosition.y) {
          return prev;
        }
        return {
          ...prev,
          [drag.kind]: nextPosition,
        };
      });
    } catch (err) {
      activeDragRef.current = null;
      const message = err?.message || 'Unable to move crop box';
      setError(message);
      setSnackbar({ open: true, message, severity: 'error' });
    }
  };

  const handleCanvasPointerUp = () => {
    activeDragRef.current = null;
  };

  const renderSliderRow = (label, field, min, max, color) => (
    <Stack spacing={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Typography sx={{ minWidth: 120, fontWeight: 600 }}>{label}</Typography>
        <Box sx={{ flex: 1, px: { xs: 0.5, sm: 1 } }}>
          <Slider
            value={cropState[field]}
            min={min}
            max={max}
            step={1}
            valueLabelDisplay="auto"
            onChange={handlePixelChange(field)}
            disabled={loading || saving || !imageLoaded}
            sx={{
              color,
              '& .MuiSlider-rail': { opacity: 0.22 },
            }}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: -1 }}>
            <Typography variant="caption" color="text.secondary">{min}</Typography>
            <Typography variant="caption" color="text.secondary">{max}</Typography>
          </Stack>
        </Box>
        <TextField
          type="number"
          size="small"
          value={cropState[field]}
          onChange={handlePixelInputChange(field)}
          disabled={loading || saving || !imageLoaded}
          inputProps={{ min, max, step: 1 }}
          sx={{ width: { xs: '100%', sm: 96 } }}
        />
      </Stack>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={loading || saving ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Face Crop Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {cameraLabel || cameraType || 'Camera'} {cameraType ? `(${cameraType})` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onFetchFrame}
            disabled={loading || saving}
            sx={{ textTransform: 'uppercase' }}
          >
            Refresh
          </Button>
          <IconButton onClick={onClose} disabled={loading || saving}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Alert severity="info">
            Use the sliders to Customize the Min and Max face crop size on the frame.
          </Alert>

          <FormControlLabel
            control={
              <Switch
                checked={cropState.enable}
                onChange={(event) => setCropState((prev) => ({ ...prev, enable: event.target.checked }))}
                disabled={loading || saving}
              />
            }
            label="Enable face crop configuration"
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Box
            ref={containerRef}
            sx={{
              width: '100%',
              minHeight: 340,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              position: 'relative',
            }}
          >
            {loading && (
              <Stack alignItems="center" spacing={1.5}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">Loading camera frame...</Typography>
              </Stack>
            )}

            {!loading && imageUrl && (
              <canvas
                ref={canvasRef}
                onMouseDown={handleCanvasPointerDown}
                onMouseMove={handleCanvasPointerMove}
                onMouseUp={handleCanvasPointerUp}
                onMouseLeave={handleCanvasPointerUp}
                onTouchStart={handleCanvasPointerDown}
                onTouchMove={handleCanvasPointerMove}
                onTouchEnd={handleCanvasPointerUp}
                onTouchCancel={handleCanvasPointerUp}
                style={{
                  width: displaySize.width ? `${displaySize.width}px` : '100%',
                  height: displaySize.height ? `${displaySize.height}px` : 'auto',
                  maxWidth: '100%',
                  borderRadius: 8,
                  display: 'block',
                  cursor: 'move',
                  touchAction: 'none',
                }}
              />
            )}

            {!loading && !imageUrl && (
              <Stack alignItems="center" spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  No frame available
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={onFetchFrame}
                  disabled={saving}
                >
                  Fetch Frame
                </Button>
              </Stack>
            )}
          </Box>

          <Stack spacing={2}>
            {renderSliderRow('Min Face Crop', 'minPixels', sliderMin, frameLimit, '#f97316')}
            {renderSliderRow('Max Face Crop', 'maxPixels', cropState.minPixels, frameLimit, '#ef4444')}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading || saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading || saving || !imageLoaded}
        >
          Save Face Crop
        </Button>
      </DialogActions>

      <Dialog
        open={saveConfirmOpen}
        onClose={saving ? undefined : () => setSaveConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 2,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'rgba(64, 81, 138, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#40518A',
            }}
          >
            <SaveIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Save Face Crop Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cameraType ? `${cameraType} camera` : cameraLabel || 'Camera'}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Alert severity="warning" sx={{ alignItems: 'flex-start' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Are you sure you want to save this face crop configuration?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This Face Crop Configuration will be reflected in the Detect app after restarting the Detect app.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setSaveConfirmOpen(false)}
            disabled={saving}
            sx={{ textTransform: 'uppercase' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleConfirmSave}
            disabled={saving}
            sx={{
              textTransform: 'uppercase',
              bgcolor: '#40518A',
              '&:hover': { bgcolor: '#32406d' },
            }}
          >
            Save Face Crop
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default FaceCropModal;
